import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppBarCMS } from '../../../../../basic';
import { Spinner } from '../../../../../common';
import {
  useGetAttendanceEventQuery,
  useSaveAttendancePointsMutation,
  useDeleteAttendancePointsMutation,
  useGetAttendancePointDetailForEventQuery,
} from '../../../../../../redux/services/attendance';
import { useGetUsersQuery } from '../../../../../../redux/services/users';
import { routes } from '../../../../../../utils/helpers/ApiClient';
import AttendanceEventDetailTable from './AttendanceEventDetailTable';
import AttendanceEventDetailStudentsForm from './AttendanceEventStudentsForm';
import { useToastMessages } from '../../../../../../hooks/useToastMessages';

const AttendanceEventDetail = () => {
  const { att_event_id } = useParams();
  const { t } = useTranslation();

  // Fetching data
  const { data: event, isLoading: isLoadingEvent } = useGetAttendanceEventQuery(
    att_event_id,
    {
      skip: !att_event_id,
    },
  );

  const {
    data: points,
    isLoading: isLoadingPoints,
    refetch: refetchPoints,
  } = useGetAttendancePointDetailForEventQuery(att_event_id, {
    skip: !att_event_id,
  });

  const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery();

  // Mutations
  const [
    saveAttendancePoints,
    {
      isSuccess: saveSuccess,
      isError: saveError,
      error: saveErrorData,
      reset: resetSaveSuccess,
    },
  ] = useSaveAttendancePointsMutation();

  const [
    deleteAttendancePoints,
    {
      isSuccess: deleteSuccess,
      isError: deleteError,
      error: deleteErrorData,
      reset: resetDeleteSuccess,
    },
  ] = useDeleteAttendancePointsMutation();

  // Local state to hold the updated points
  const [localPoints, setLocalPoints] = useState(points || []);

  const {
    saveSuccess: toastSaveSuccess,
    deleteSuccess: toastDeleteSuccess,
    apiErrorMessage,
  } = useToastMessages();

  const allusers = useMemo(() => {
    if (!users) return [];
    return users.map((person) => ({
      label: `${person.title_before}${person.first_name} ${person.last_name}${person.title_after} (${person.departmentOrStudyGroup})`,
      value: person.person_id,
      last_name: person.last_name,
    }));
  }, [users]);

  useEffect(() => {
    setLocalPoints(points);
  }, [points]);

  useEffect(() => {
    if (saveSuccess && !isLoadingPoints) {
      toastSaveSuccess();
      refetchPoints();
      resetSaveSuccess();
    }
  }, [saveSuccess, isLoadingPoints]);

  useEffect(() => {
    if (saveError && saveErrorData) {
      apiErrorMessage(saveErrorData);
      resetSaveSuccess();
    }
  }, [saveError, saveErrorData]);

  useEffect(() => {
    if (deleteSuccess && !isLoadingPoints) {
      toastDeleteSuccess();
      refetchPoints();
      resetDeleteSuccess();
    }
  }, [deleteSuccess, isLoadingPoints]);

  useEffect(() => {
    if (deleteError && deleteErrorData) {
      apiErrorMessage(deleteErrorData);
      resetDeleteSuccess();
    }
  }, [deleteError, deleteErrorData]);

  if (isLoadingEvent || isLoadingUsers || isLoadingPoints) {
    return <Spinner />;
  }

  const onSubmit = (values) => {
    const addUser = {
      att_event_id: event.id,
      user_info: {
        card_number: values.card_number ?? null,
        person_id: values.person_id ?? null,
      },
    };
    saveAttendancePoints(addUser);
  };

  const onDelete = (row) => {
    deleteAttendancePoints({ id: row.id });
  };

  const changePoints = (all = false, type, row = undefined) => {
    if (all && !row) {
      saveAttendancePoints({
        att_event_id: event.id,
        type: 'all-users',
        action: type,
      });
    } else if (row) {
      saveAttendancePoints({
        person_id: row.person_id.person_id,
        att_event_id: event.id,
        type: 'user',
        action: type, // 'add' or 'substract'
      });
    }
  };

  return (
    <>
      <AppBarCMS
        title={t('Detail udalosti')}
        backUrl={routes.cmsAttendanceMyEvents}
        backUrlTooltip={t('Moje udalosti')}
        permissionsBasic={'attendance-events.show'}
      />
      <div style={{ margin: '10px' }}>
        <AttendanceEventDetailTable event={event} />
        <AttendanceEventDetailStudentsForm
          students={localPoints}
          allUsers={allusers}
          changePoints={changePoints}
          onSubmit={onSubmit}
          onDelete={onDelete}
          isLoading={isLoadingPoints}
        />
      </div>
    </>
  );
};

export default AttendanceEventDetail;
